import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ChevronRight,
    CheckCircle2,
    Trees,
    Wrench,
    ClipboardCheck,
    ShieldCheck,
    BarChart3
} from 'lucide-react';
import { SEMANTIC_SUCCESS_MID } from '../../theme/chartColors';
import './AuditManagement.css';

const AuditManagement = () => {
    useEffect(() => {
        document.title = "Audit Management & Compliance | X-Max";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', 'Streamline your audit processes with X-Max Audit Management. From 5S and JH audits to internal and regulatory compliance, gain actionable insights for continuous improvement.');
        }
        window.scrollTo(0, 0);
    }, []);

    const auditTypes = [
        {
            title: "5S Audits",
            desc: "Drives workplace organisation and standardisation, enhancing efficiency and reducing waste.",
            icon: <Trees size={32} />,
            accent: "#a78bfa"
        },
        {
            title: "JH Audits",
            desc: "Empowers operators with autonomous maintenance checks to proactively identify and address minor equipment issues.",
            icon: <Wrench size={32} />,
            accent: "#f97316"
        },
        {
            title: "Internal & External Audits",
            desc: "Manages adherence to internal policies and external regulatory standards for complete compliance.",
            icon: <ClipboardCheck size={32} />,
            accent: "#6366f1"
        },
        {
            title: "Safety Audits",
            desc: "Ensures rigorous verification of safety protocols, mitigating risks and fostering a secure work environment.",
            icon: <ShieldCheck size={32} />,
            accent: SEMANTIC_SUCCESS_MID
        }
    ];

    return (
        <div className="audit-page">
            {/* Hero Section */}
            <section className="audit-hero">
                <div className="container audit-hero-container">
                    <div className="audit-hero-content">
                        <div className="sec-tag">CORE MODULES</div>
                        <h1>Audit <span className="gradient-text">Management</span></h1>
                        <p className="hero-desc">
                            X-Max's Audit Management module provides a unified platform to streamline all types of audits, transforming compliance checks into actionable insights for continuous improvement.
                        </p>
                        <p className="hero-subtext">
                            From workplace organisation to critical safety protocols, our system ensures thorough, consistent, and transparent audit processes across your operations.
                        </p>
                        <Link to="/book-now" className="btn btn-primary btn-lg">
                            Book Demo <ChevronRight size={20} />
                        </Link>
                    </div>
                    <div className="audit-hero-visual">
                        <div className="visual-wrapper">
                            <img
                                src="./assets/modules/oeeone.png"
                                alt="Audit Management Dashboard"
                                className="hero-img"
                            />
                            <div className="visual-overlay"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Audit Pillars Section */}
            <section className="audit-pillars">
                <div className="container">
                    <div className="section-header center">
                        <div className="sec-tag">COMPLIANCE PILLARS</div>
                        <h2>Comprehensive <span className="gradient-text">Audit Solutions</span></h2>
                    </div>

                    <div className="pillars-grid">
                        {auditTypes.map((audit, index) => (
                            <div key={index} className="audit-pillar-card">
                                <div className="card-icon-box" style={{ color: audit.accent }}>
                                    {audit.icon}
                                </div>
                                <h3>{audit.title}</h3>
                                <p>{audit.desc}</p>
                                <div className="card-accent-line" style={{ background: audit.accent }}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Bottom Content Section */}
            <section className="audit-summary">
                <div className="container">
                    <div className="summary-card">
                        <div className="summary-content">
                            <p>
                                By centralising audit processes, X-Max ensures <strong>accountability</strong>, simplifies reporting, and provides clear visibility into areas requiring attention, enabling a proactive approach to operational excellence and risk mitigation.
                            </p>
                            <ul className="summary-features">
                                <li><CheckCircle2 size={18} /> Digital scoring & instant reporting</li>
                                <li><CheckCircle2 size={18} /> Photographic evidence capture</li>
                                <li><CheckCircle2 size={18} /> Root cause analysis integration</li>
                                <li><CheckCircle2 size={18} /> Real-time compliance dashboards</li>
                            </ul>
                        </div>
                        <div className="summary-visual">
                            <BarChart3 size={150} className="floating-icon" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="audit-footer-cta">
                <div className="container">
                    <div className="cta-card">
                        <h2>Ready to transform your compliance checks into actionable excellence?</h2>
                        <Link to="/book-now" className="btn btn-primary btn-lg">Schedule Operational Audit</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AuditManagement;
